// Web MIDI input plumbing.
//
// Responsibilities:
//   - Request MIDI access on construction (no-op if the browser doesn't
//     support Web MIDI, e.g. older Safari/Firefox versions).
//   - Enumerate available input devices and notify on hot-plug.
//   - Translate raw MIDI byte streams from the selected device into
//     press/release calls on the supplied PianoKeyboard.
//
// Supported MIDI messages:
//   - Note On  (0x90 + channel): press(midi, velocity = data2 / 127)
//   - Note Off (0x80 + channel): release(midi)
//   - Note On with velocity 0 (some controllers send this in place of Note Off): release(midi)
// All other messages (CC, pitch bend, sysex, clock) are ignored for now.

import type { PianoKeyboard } from "./keyboard.ts";

export interface MidiInputInfo {
  /** Stable id used by the dropdown and by `selectInput`. */
  id: string;
  /** Display name (e.g. "MPK Mini Mk3"). */
  name: string;
  /** Manufacturer string from the device, if reported. */
  manufacturer?: string;
}

export interface MidiManagerOptions {
  piano: PianoKeyboard;
  /** Called when the device list changes (hot-plug). */
  onDevicesChange?: (devices: MidiInputInfo[]) => void;
  /** Called when MIDI access is unavailable (unsupported browser or denied). */
  onUnavailable?: (reason: string) => void;
}

/** Status of the MIDI access request. */
export type MidiStatus = "pending" | "unsupported" | "denied" | "available";

export class MidiManager {
  private piano: PianoKeyboard;
  private onDevicesChange: ((devices: MidiInputInfo[]) => void) | undefined;
  private onUnavailable: ((reason: string) => void) | undefined;
  private access: MIDIAccess | null = null;
  private currentInput: MIDIInput | null = null;
  private status: MidiStatus = "pending";
  private boundMidiMessage: (ev: MIDIMessageEvent) => void;

  constructor(opts: MidiManagerOptions) {
    this.piano = opts.piano;
    this.onDevicesChange = opts.onDevicesChange;
    this.onUnavailable = opts.onUnavailable;
    this.boundMidiMessage = (ev) => this.handleMidiMessage(ev);
  }

  /** Request Web MIDI access. Resolves regardless of grant — check
   *  `getStatus()` afterward. */
  async init(): Promise<void> {
    const requestMIDIAccess = (
      navigator as Navigator & {
        requestMIDIAccess?: (opts?: { sysex?: boolean }) => Promise<MIDIAccess>;
      }
    ).requestMIDIAccess;

    if (typeof requestMIDIAccess !== "function") {
      this.status = "unsupported";
      this.onUnavailable?.("Web MIDI is not supported in this browser.");
      return;
    }
    try {
      this.access = await requestMIDIAccess.call(navigator, { sysex: false });
      this.status = "available";
      this.access.onstatechange = (): void => this.emitDevices();
      this.emitDevices();
    } catch (err) {
      this.status = "denied";
      this.onUnavailable?.(
        `MIDI access denied: ${err instanceof Error ? err.message : String(err)}`,
      );
    }
  }

  getStatus(): MidiStatus {
    return this.status;
  }

  listInputs(): MidiInputInfo[] {
    if (!this.access) return [];
    const inputs: MidiInputInfo[] = [];
    for (const input of this.access.inputs.values()) {
      inputs.push({
        id: input.id,
        name: input.name ?? "MIDI input",
        ...(input.manufacturer ? { manufacturer: input.manufacturer } : {}),
      });
    }
    return inputs;
  }

  /** Switch the active MIDI input device. Pass null to disconnect from MIDI. */
  selectInput(deviceId: string | null): void {
    this.detachCurrent();
    if (!deviceId || !this.access) return;
    const input = this.access.inputs.get(deviceId);
    if (!input) return;
    this.currentInput = input;
    input.onmidimessage = this.boundMidiMessage;
  }

  private detachCurrent(): void {
    if (this.currentInput) {
      this.currentInput.onmidimessage = null;
      // Release any MIDI-held notes so they don't get stuck on disconnect.
      this.piano.releaseAll();
      this.currentInput = null;
    }
  }

  private emitDevices(): void {
    if (!this.onDevicesChange) return;
    this.onDevicesChange(this.listInputs());
  }

  private handleMidiMessage(ev: MIDIMessageEvent): void {
    const data = ev.data;
    if (!data || data.length < 2) return;
    const status = data[0]! & 0xf0;
    const note = data[1]!;
    const value = data.length > 2 ? data[2]! : 0;

    if (status === 0x90 && value > 0) {
      // Note On.
      this.piano.press(`m:${note}`, note, value / 127);
    } else if (status === 0x80 || (status === 0x90 && value === 0)) {
      // Note Off (explicit or velocity-0 form).
      this.piano.release(`m:${note}`);
    }
    // Other messages ignored for now (CC, pitch bend, sysex, clock).
  }
}
